import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { ResizableAside } from '@risksmart-app/atomic-ui';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { ContributorType as Contributor_Type_Enum } from '@risksmart-app/domain/src/types/consts/contributor-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ActionsButton from 'src/components/actions-button';
import AddToEnterpriseRiskModal from 'src/components/add-to-enterprise-risk-modal/AddToEnterpriseRiskModal';
import RemoveFromEnterpriseRiskModal from 'src/components/add-to-enterprise-risk-modal/RemoveFromEnterpriseRiskModal';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { useInitiateWizard } from 'src/components/wizard/hooks/useInitiateWizard';
import { useStartWizard } from 'src/components/wizard/hooks/useStartWizard';
import { CreateAssessmentModal } from 'src/components/wizard/modals/CreateAssessmentModal';
import { LinkAssessmentModal } from 'src/components/wizard/modals/LinkAssessmentModal';
import { WizardButton } from 'src/components/wizard/WizardButton';
import { PageLayout } from 'src/layouts';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import { useGetDetailParentPath } from 'src/routes/useGetDetailParentPath';

import { useDeleteRisk } from '@/hooks/mutations/risk';
import { useGetRiskById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';

import { RiskOverviewPanel } from '../blocks/risk-overview-panel';
import { buildEnterpriseRiskActions } from './enterpriseRiskActions';
import DetailsAside from './tabs/details/DetailsAside';
import useExporter from './useExporter';

type Props = {
  selectedTabId:
    | 'acceptances'
    | 'actions'
    | 'appetites'
    | 'approvals'
    | 'controls'
    | 'details'
    | 'impacts'
    | 'indicators'
    | 'linkedItems'
    | 'notificationHistory'
    | 'ratings';
  showDeleteButton?: boolean;
};

const Page: FC<Props> = ({ selectedTabId, showDeleteButton }) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isLinkAssessmentModalVisible, setIsLinkAssessmentModalVisible] =
    useState(false);
  const [isCreateAssessmentModalVisible, setIsCreateAssessmentModalVisible] =
    useState(false);

  const [
    isAddToEnterpriseRiskModalVisible,
    setIsAddToEnterpriseRiskModalVisible,
  ] = useState(false);
  const [isAddToEntityModalVisible, setIsAddToEntityModalVisible] =
    useState(false);
  const [
    isRemoveFromEnterpriseRiskModalVisible,
    setIsRemoveFromEnterpriseRiskModalVisible,
  ] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  // Sidebar-preferences opener bridged up from the RiskOverviewPanel (relocate
  // mode) — drives the toolbar's "Edit sidebar preferences" action.
  const sidebarPrefsConfig = useObjectToolbarFormStore(
    (s) => s.sidebarPrefsConfigState
  );
  const navigate = useNavigate();
  const riskId = useGetGuidParam('riskId');
  const params = useParams();
  const { user } = useRisksmartUser();

  const parentUrl = useGetDetailParentPath(riskId);
  const { deleteRisk, loading: deleteLoading } = useDeleteRisk();
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'risks' });
  const { data, error } = useGetRiskById({ queryArgs: { riskId } });
  if (error) {
    throw error;
  }

  const risk = data?.risk[0];
  const oversightEnabled = useIsModuleEnabled(
    'assessment.subModules.oversight_register_and_findings'
  );
  const internalAuditEnabled = useIsModuleEnabled('internal_audit_entity');
  const { hasPermission: canViewOversight, loading: canViewOversightLoading } =
    useHasPermissionQuery('read:oversight_assessment', risk);
  const {
    hasPermission: canViewInternalAudit,
    loading: canViewInternalAuditLoading,
  } = useHasPermissionQuery('read:internal_audit_report', risk);
  const canShowInternalAudit =
    internalAuditEnabled &&
    canViewInternalAudit &&
    !canViewInternalAuditLoading;
  const canShowOversight =
    oversightEnabled && canViewOversight && !canViewOversightLoading;
  const [exportItem, { loading: exporting }] = useExporter(
    riskId,
    canShowInternalAudit,
    canShowOversight
  );
  const tabs = useTabs({
    parentType: Parent_Type_Enum.Risk,
    parent: risk,
    hrefRoot: `/risks/${riskId}`,
    disabled: !risk,
  });
  const tabsWithAside = tabs.map((tab) =>
    tab.id === 'loading'
      ? tab
      : {
          ...tab,
          content: (
            <ResizableAside
              storageKey={'risk-page-aside-width'}
              initialWidth={400}
              minWidth={260}
              maxWidth={640}
              gapClassName={'gap-3'}
              aside={
                <>
                  <RiskOverviewPanel
                    riskId={riskId}
                    canViewOversight={canShowOversight}
                    canViewInternalAudit={canShowInternalAudit}
                  />
                  {tab.id === 'details' && <DetailsAside riskId={riskId} />}
                </>
              }
            >
              {tab.content}
            </ResizableAside>
          ),
        }
  );
  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      if (!risk) {
        return false;
      }
      await deleteRisk(risk.Id);
      await navigate(parentUrl);

      return true;
    },
  });

  if (data?.risk.length === 0) {
    throw new PageNotFound(`Risk with id ${riskId} not found`);
  }

  const counter =
    risk && `(${getFriendlyId(Parent_Type_Enum.Risk, risk.SequentialId)})`;
  const fallbackTitle = st('fallback_title');

  const wizardFeatureEnabled = useIsModuleEnabled(
    'risk.subModules.rcsa_wizard'
  );
  const { hasPermission: canViewWizard, loading: canViewWizardLoading } =
    useHasPermissionQuery('update:risk', risk);

  const isOwnerOrContributor =
    risk?.ancestorContributors?.some(
      (contributor) =>
        contributor.UserId === user?.userId &&
        (contributor.ContributorType === Contributor_Type_Enum.Owner ||
          contributor.ContributorType === Contributor_Type_Enum.Contributor)
    ) ?? false;

  useInitiateWizard(
    { riskId: riskId, title: risk?.Title ?? '' },
    isOwnerOrContributor,
    wizardFeatureEnabled
  );

  const wizardButtonVisible =
    !canViewWizardLoading &&
    wizardFeatureEnabled &&
    canViewWizard &&
    isOwnerOrContributor;

  const wizardBasePath = `${parentUrl}/${params.riskId}`;
  const onStartNewWizard = useCallback(
    () => setIsLinkAssessmentModalVisible(true),
    []
  );
  const {
    isWizardInProgress,
    loading: wizardStarting,
    startWizard,
  } = useStartWizard({
    basePath: wizardBasePath,
    riskId,
    onStartNew: onStartNewWizard,
  });

  const isEnterpriseRiskEnabled = useIsModuleEnabled('enterprise_risk');
  const {
    hasPermission: canLinkToEnterpriseRisk,
    loading: canLinkToEnterpriseRiskLoading,
  } = useHasPermissionQuery('insert:enterprise_risk');
  const { hasPermission: canLinkToEntity, loading: canLinkToEntityLoading } =
    useHasPermissionQuery('update:risk');
  const {
    hasPermission: canUnlinkEnterpriseRisk,
    loading: canUnlinkEnterpriseRiskLoading,
  } = useHasPermissionQuery('update:enterprise_risk');

  const hasEnterpriseRiskLink = Boolean(
    risk?.enterpriseRiskInstance?.EnterpriseRiskId ??
    risk?.enterpriseRiskInstance?.EntityId
  );

  const {
    hasPermission: hasPermissionToDelete,
    loading: hasPermissionToDeleteLoading,
  } = useHasPermissionQuery('delete:risk', risk);

  // Legacy header actions — built only when the object toolbar is off.
  // `headerOverride` replaces the `actions` prop entirely when the flag is on
  // (PageLayout: `headerOverride ?? (...)`), so building the button array +
  // ActionsButton on a flag-on render would be discarded work (CUS-152 review).
  let actions: ReactNode[] = [];
  if (!objectToolbarEnabled) {
    const buttons = [
      <Button
        iconName={'download'}
        disabled={exporting}
        onClick={exportItem}
        key={'export'}
      >
        {t('export.export')}
      </Button>,
    ];

    if (
      isEnterpriseRiskEnabled &&
      canLinkToEnterpriseRisk &&
      !canLinkToEnterpriseRiskLoading
    ) {
      buttons.push(
        <Button
          variant={'normal'}
          formAction={'none'}
          key={'addToEnterpriseRisk'}
          onClick={() => {
            setIsAddToEnterpriseRiskModalVisible(true);
          }}
        >
          {t('enterpriseRisks.addRiskToEnterpriseRisk.linkToEnterpriseRisk')}
        </Button>
      );
    }

    if (isEnterpriseRiskEnabled && canLinkToEntity && !canLinkToEntityLoading) {
      buttons.push(
        <Button
          variant={'normal'}
          formAction={'none'}
          key={'addToEntity'}
          onClick={() => {
            setIsAddToEntityModalVisible(true);
          }}
        >
          {t('enterpriseRisks.addRiskToEnterpriseRisk.linkToEntity')}
        </Button>
      );
    }

    if (
      isEnterpriseRiskEnabled &&
      hasEnterpriseRiskLink &&
      canUnlinkEnterpriseRisk &&
      !canUnlinkEnterpriseRiskLoading
    ) {
      buttons.push(
        <Button
          variant={'normal'}
          formAction={'none'}
          key={'removeFromEnterpriseRisk'}
          onClick={() => {
            setIsRemoveFromEnterpriseRiskModalVisible(true);
          }}
        >
          {risk?.enterpriseRiskInstance?.EnterpriseRiskId
            ? t(
                'enterpriseRisks.removeRiskFromEnterpriseRisk.removeFromEnterpriseRisk'
              )
            : t(
                'enterpriseRisks.removeRiskFromEnterpriseRisk.removeFromEntity'
              )}
        </Button>
      );
    }

    // Always last so it shows on the right for consistency
    if (
      showDeleteButton &&
      hasPermissionToDelete &&
      !hasPermissionToDeleteLoading
    ) {
      buttons.push(
        <Button
          variant={'normal'}
          formAction={'none'}
          key={'delete'}
          // Hydration gate — same as the toolbar danger action. The permission
          // query can resolve before `risk`, so without this the button is live
          // while `onDelete` still short-circuits on `if (!risk) return false`,
          // issuing no request and toasting a bare "Failed to save changes"
          // (ENG-616).
          disabled={!risk}
          onClick={() => {
            setIsDeleteModalVisible(true);
          }}
        >
          {st('delete_button')}
        </Button>
      );
    }

    actions = buttons;

    // More than 2 buttons, show actions dropdown
    if (
      (wizardButtonVisible && buttons.length > 1) ||
      (!wizardButtonVisible && buttons.length > 2)
    ) {
      actions = [
        <ActionsButton
          key={'actions'}
          buttonText={'Actions'}
          items={buttons.map((b) => {
            return {
              text: b.props.children,
              id: b.props.children,
              // Carry `disabled` across the collapse. Without it every button's
              // disabled state (the hydration gate on delete, `exporting` on
              // export) is silently dropped the moment there are enough buttons
              // to fold into the dropdown, so the item stays clickable while the
              // button it came from would have been inert (ENG-616).
              disabled: b.props.disabled,
              onItemClick: b.props.onClick,
            };
          })}
        />,
      ];
    }
  }

  // Object-level toolbar (CUS-152) — flag-gated. Relocates the header actions
  // into the toolbar tray/overflow: the wizard opener (primary), Edit form/tabs
  // + enterprise-risk link/unlink (overflow), Export (download group) and Delete
  // (danger). Enterprise-risk gates resolve against a different object type than
  // this risk, so they are gated at build time via the existing
  // `useHasPermissionQuery` booleans rather than the toolbar's `permission` (the
  // resolver keys instance-level permissions to `parentObject`'s type). The Save
  // disc/Cancel come from the active details form via the toolbar bridge store.
  const toolbarConfig: ObjectToolbarConfig = useMemo(() => {
    const enterpriseRiskActions = buildEnterpriseRiskActions({
      enabled: isEnterpriseRiskEnabled,
      canLinkToEnterpriseRisk,
      canLinkToEnterpriseRiskLoading,
      canLinkToEntity,
      canLinkToEntityLoading,
      hasEnterpriseRiskLink,
      canUnlinkEnterpriseRisk,
      canUnlinkEnterpriseRiskLoading,
      isLinkedToEnterpriseRisk: Boolean(
        risk?.enterpriseRiskInstance?.EnterpriseRiskId
      ),
      labels: {
        addToEnterpriseRisk: t(
          'enterpriseRisks.addRiskToEnterpriseRisk.linkToEnterpriseRisk'
        ),
        addToEntity: t('enterpriseRisks.addRiskToEnterpriseRisk.linkToEntity'),
        removeFromEnterpriseRisk: t(
          'enterpriseRisks.removeRiskFromEnterpriseRisk.removeFromEnterpriseRisk'
        ),
        removeFromEntity: t(
          'enterpriseRisks.removeRiskFromEnterpriseRisk.removeFromEntity'
        ),
      },
      handlers: {
        onAddToEnterpriseRisk: () => setIsAddToEnterpriseRiskModalVisible(true),
        onAddToEntity: () => setIsAddToEntityModalVisible(true),
        onRemove: () => setIsRemoveFromEnterpriseRiskModalVisible(true),
      },
    });

    return {
      header: {
        title: risk?.Title ?? fallbackTitle,
        counter: counter || undefined,
      },
      parentObject: risk,
      save: true,
      actions: {
        ...(wizardButtonVisible
          ? {
              primary: [
                {
                  id: 'wizard',
                  // New vs Continue, same as the legacy WizardButton: an RCSA
                  // already in progress resumes into the wizard sidebar rather
                  // than trying to create a second one (CUS-363).
                  label: isWizardInProgress
                    ? t('wizard.continueWizardTitle')
                    : t('wizard.startWizardTitle'),
                  iconName: 'wand' as const,
                  onClick: startWizard,
                  disabled: wizardStarting,
                },
              ],
            }
          : {}),
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01' as const,
                onClick: () => setShowTabSettings(true),
              },
              ...(sidebarPrefsConfig
                ? [
                    {
                      id: 'edit-sidebar-preferences',
                      label: t('objectToolbar.sidebarSettings'),
                      iconName: 'settings-04' as const,
                      onClick: sidebarPrefsConfig.openPreferences,
                    },
                  ]
                : []),
            ],
          },
          ...(enterpriseRiskActions.length > 0
            ? [{ id: 'enterprise-risk', actions: enterpriseRiskActions }]
            : []),
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01' as const,
                onClick: exportItem,
                disabled: exporting,
              },
            ],
          },
        ],
        // Delete is same-type as `parentObject` (the risk), so it uses the
        // resolver's `permission` gate (fail-closed while loading), matching the
        // Indicators pilot. `showDeleteButton` (a route prop) still decides
        // whether the action is offered at all.
        ...(showDeleteButton
          ? {
              danger: [
                {
                  id: 'delete',
                  label: st('delete_button'),
                  iconName: 'trash-01' as const,
                  permission: 'delete:risk',
                  // Wait for the entity to hydrate. `onDelete` guards on
                  // `if (!risk) return false`, which surfaces as a bare
                  // "Failed to save changes" with NO request issued, so a click
                  // inside the hydration window looks like a broken delete
                  // (ENG-616). Disabling also restores Playwright's actionability
                  // wait.
                  disabled: !risk,
                  onClick: () => setIsDeleteModalVisible(true),
                },
              ],
            }
          : {}),
      },
    };
  }, [
    risk,
    counter,
    fallbackTitle,
    t,
    st,
    formConfig,
    sidebarPrefsConfig,
    exportItem,
    exporting,
    wizardButtonVisible,
    isWizardInProgress,
    wizardStarting,
    startWizard,
    isEnterpriseRiskEnabled,
    canLinkToEnterpriseRisk,
    canLinkToEnterpriseRiskLoading,
    canLinkToEntity,
    canLinkToEntityLoading,
    hasEnterpriseRiskLink,
    canUnlinkEnterpriseRisk,
    canUnlinkEnterpriseRiskLoading,
    showDeleteButton,
  ]);

  return (
    <PageLayout
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          {wizardButtonVisible && (
            <>
              <WizardButton
                basePath={wizardBasePath}
                riskId={riskId}
                onClick={onStartNewWizard}
              />
            </>
          )}
          {actions}
        </SpaceBetween>
      }
      meta={{
        title: fallbackTitle,
      }}
      title={risk?.Title}
      counter={counter}
    >
      <ControlledTabs
        tabs={tabsWithAside}
        activeTabId={selectedTabId}
        variant={'container'}
        parentType={Parent_Type_Enum.Risk}
        parent={risk}
        disableSettings={objectToolbarEnabled}
      />

      <TabSettingsModal
        parentType={Parent_Type_Enum.Risk}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={risk}
      />

      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>

      <AddToEnterpriseRiskModal
        riskId={riskId}
        enterpriseRiskId={risk?.enterpriseRiskInstance?.EnterpriseRiskId}
        entityId={risk?.enterpriseRiskInstance?.EntityId}
        riskTier={risk?.Tier ?? 1}
        isVisible={
          isAddToEnterpriseRiskModalVisible || isAddToEntityModalVisible
        }
        modalSubType={
          isAddToEnterpriseRiskModalVisible ? 'enterpriseRisk' : 'entity'
        }
        onDismiss={() => {
          setIsAddToEnterpriseRiskModalVisible(false);
          setIsAddToEntityModalVisible(false);
        }}
      />
      <RemoveFromEnterpriseRiskModal
        riskId={riskId}
        isVisible={isRemoveFromEnterpriseRiskModalVisible}
        onDismiss={() => {
          setIsRemoveFromEnterpriseRiskModalVisible(false);
        }}
      />
      <LinkAssessmentModal
        isVisible={isLinkAssessmentModalVisible}
        setIsVisible={setIsLinkAssessmentModalVisible}
        setIsCreateAssessmentModalVisible={setIsCreateAssessmentModalVisible}
        basePath={wizardBasePath}
        riskId={riskId}
        riskTitle={risk?.Title ?? ''}
      />
      <CreateAssessmentModal
        isVisible={isCreateAssessmentModalVisible}
        setIsVisible={setIsCreateAssessmentModalVisible}
        basePath={wizardBasePath}
        riskId={riskId}
        riskTitle={risk?.Title ?? ''}
      />
    </PageLayout>
  );
};

export default Page;
